import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { type Category, type Product } from "@/lib/schema";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import LoadingSpinner from "../components/LoadingSpinner";
import { ProductImageCarousel } from "../components/ProductImageCarousel";
import { useRoute, useLocation } from "wouter";
import { Package2, Wrench, Truck, Factory, Settings, type LucideIcon, Search, Tag, XCircle, CheckCircle2, Zap, Settings2, ArrowRight, X, Phone, Mail, ListChecks, Sparkles, FileText, Link, Check, Calculator, Loader2, Filter, FilterX } from "lucide-react";
import ScheduleCallForm from "@/components/ScheduleCallForm";
import { useToast } from "@/hooks/use-toast";
import FinancialSimulator from "@/components/FinancialSimulator";
import AuthModal from "@/components/auth/AuthModal";
import { authService } from "@/lib/auth/authService";

// Extend Category type to include children
interface CategoryWithChildren extends Category {
  children?: CategoryWithChildren[];
}

// Default icon map
const defaultIcons: Record<string, LucideIcon> = {
  "MÁQUINAS DE ENVASADO": Package2,
  "MÁQUINAS DE PROCESO": Settings,
  "MÁQUINAS DE TRANSPORTE": Truck,
  "MÁQUINAS DE PRODUCCIÓN": Factory,
  DEFAULT: Wrench,
};

// Helper function to get icon component
const getIconComponent = (categoryName: string): LucideIcon => {
  const Icon =
    defaultIcons[categoryName.toUpperCase()] || defaultIcons["DEFAULT"];
  return Icon;
};

// Helper function to check if an image URL is valid
const isValidImageUrl = (url: string | undefined): boolean => {
  if (!url) return false;

  // Verificar que la URL no esté vacía y no contenga 'undefined' o 'null'
  if (url.trim() === '' || url.includes('undefined') || url.includes('null')) {
    return false;
  }

  // Si la URL es relativa, asumimos que es válida
  if (url.startsWith('/')) {
    return true;
  }

  // Verificar que la URL tenga un formato válido
  try {
    new URL(url);
    return true;
  } catch (e) {
    // Si no es una URL válida, podría ser una ruta relativa
    return url.startsWith('/') || url.startsWith('./') || url.startsWith('../');
  }
};

// Función para intentar diferentes formatos de URL de imagen
const getAlternativeImageUrl = (img: string): string | null => {
  // Si ya es una URL completa que no es de Supabase, devolverla tal cual
  if (img.startsWith('http') && !img.includes('supabase.co')) {
    return img;
  }

  // Si es una URL de Supabase, extraer la ruta del archivo
  if (img.includes('supabase.co') && img.includes('/storage/')) {
    const pathMatch = img.match(/\/images\/(.+)$/);
    if (pathMatch && pathMatch[1]) {
      // Construir URL directa
      const filePath = pathMatch[1];

      // Si la ruta ya incluye 'products/', usarla tal cual
      if (filePath.includes('products/')) {
        return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${filePath}`;
      }

      // Si no, asumir que es un nombre de archivo y añadir la ruta products/
      return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${filePath}`;
    }
  }

  // Si es una ruta relativa, construir URL completa
  if (!img.startsWith('http')) {
    // Limpiar la ruta
    const cleanPath = img.startsWith('/') ? img.substring(1) : img;

    // Si la ruta ya incluye 'products/', usarla tal cual
    if (cleanPath.includes('products/')) {
      return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${cleanPath}`;
    }

    // Si no, asumir que es un nombre de archivo y añadir la ruta products/
    return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${cleanPath}`;
  }

  // Extraer el nombre del archivo de cualquier URL
  if (img.includes('/')) {
    // Obtener el nombre del archivo de la ruta
    const parts = img.split('/');
    const fileName = parts[parts.length - 1];
    return `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${fileName}`;
  }

  return null;
};

export default function NewMachinery() {
  const { toast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showScheduleCallForm, setShowScheduleCallForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [showFinancialSimulator, setShowFinancialSimulator] = useState(false);
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  // Estado para controlar si el usuario está registrado
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  // Estado para el modal de autenticación
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Estado para controlar si el panel de información está visible en el modal de producto
  const [showInfoPanel, setShowInfoPanel] = useState(true);
  // Estado para almacenar todos los productos cargados inicialmente
  const [allProducts, setAllProducts] = useState<Product[]>([]); // Nuevo estado para todos los productos

  // Obtener la ubicación actual y función para navegar
  const [location, setLocation] = useLocation();

  // Comprobar si el usuario está autenticado al cargar la página
  useEffect(() => {
    const checkAuthStatus = async () => {
      const isAuthenticated = authService.isAuthenticated();
      setIsUserLoggedIn(isAuthenticated);
    };

    checkAuthStatus();
  }, []);

  // Verificar si estamos en alguna de las rutas específicas
  const [matchCategory, paramsCategory] = useRoute("/new/category/:categoryId");
  const [matchSubcategory, paramsSubcategory] = useRoute("/new/category/:categoryId/subcategory/:subcategoryId");
  const [matchProduct, paramsProduct] = useRoute("/new/category/:categoryId/subcategory/:subcategoryId/product/:productId");

  // Variable para almacenar el ID del producto de la URL
  const [urlProductId, setUrlProductId] = useState<number | null>(null);

  // Fetch the complete category hierarchy
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useQuery<CategoryWithChildren[]>({
    queryKey: ["categories-hierarchy"],
    queryFn: async () => {
      const response = await fetch("https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/marketplace/categories/hierarchy");
      if (!response.ok) {
        throw new Error("Error al cargar las categorías");
      }
      return response.json();
    },
    staleTime: Infinity,
    retry: 3
  });

  // Fetch ALL products initially
  const { data: initialProductsData, isLoading: allProductsLoading, error: productsError } = useQuery<Product[]>({ // Renombrar data y isLoading
    queryKey: [`all-products-new`], // Clave de consulta para todos los productos
    queryFn: async () => {
      // Usar el nuevo endpoint para obtener todos los productos 'new'
      const url = `https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/marketplace/products/condition/new`;
      console.log("Loading ALL products from:", url);
      const response = await fetch(url);
      if (!response.ok) {
        console.error("Error loading all products:", response.status, await response.text());
        throw new Error("Error al cargar todos los productos");
      }
      const data = await response.json();
      console.log("All products loaded:", data.length);
      // Aquí asumimos que la Edge Function ya devuelve los productos procesados (camelCase, etc.)
      return data;
    },
    staleTime: Infinity, // Mantener en caché
    retry: 3,
    /* onSuccess no es una opción directa en v5+, mover a useEffect
    onSuccess: (data) => {
      setAllProducts(data || []); // Guardar en el estado local
    },
    */
  });

  // Actualizar allProducts cuando initialProductsData cambie
  useEffect(() => {
    if (initialProductsData) {
      setAllProducts(initialProductsData);
      console.log("Estado allProducts actualizado:", initialProductsData.length);
    }
  }, [initialProductsData]);

  // Detectar parámetros de la URL al cargar la página
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productIdFromQuery = params.get('product');

    console.log("URL params detected:", {
      matchProduct,
      paramsProduct,
      matchSubcategory,
      paramsSubcategory,
      matchCategory,
      paramsCategory
    });

    // Si estamos en una ruta de producto específico
    if (matchProduct && paramsProduct?.productId) {
      // Extraer el ID del producto de la URL (formato: "107-nombre-producto")
      const productIdStr = paramsProduct.productId.split('-')[0];
      const productId = parseInt(productIdStr);
      console.log("Product ID from URL path:", productId);

      // Guardar los parámetros de la URL
      setUrlProductId(productId);

      // Establecer la categoría y subcategoría desde los parámetros de la URL
      if (paramsProduct?.categoryId) {
        setSelectedCategory(parseInt(paramsProduct.categoryId));
      }

      if (paramsProduct?.subcategoryId) {
        setSelectedSubcategory(parseInt(paramsProduct.subcategoryId));
      }

      // Importante: Forzar la carga de productos para esta ruta
      const loadProductData = async () => {
        try {
          const response = await fetch(`https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/marketplace/products/condition/new`);
          if (!response.ok) {
            throw new Error("Error al cargar los productos");
          }
          const data = await response.json();
          console.log("Productos cargados directamente:", data.length);

          // Buscar el producto específico
          const product = data.find((p: { id: number }) => p.id === productId);
          if (product) {
            console.log("Producto encontrado directamente:", product.name);
            setSelectedProduct(product);
            setCurrentImageIndex(0);
          } else {
            console.log("No se encontró el producto con ID:", productId);
          }
        } catch (error) {
          console.error("Error cargando productos:", error);
        }
      };

      loadProductData();
    }
    // Si estamos en una ruta de subcategoría
    else if (matchSubcategory && paramsSubcategory?.subcategoryId) {
      setSelectedCategory(paramsSubcategory.categoryId ? parseInt(paramsSubcategory.categoryId) : null);
      setSelectedSubcategory(parseInt(paramsSubcategory.subcategoryId));
    }
    // Si estamos en una ruta de categoría
    else if (matchCategory && paramsCategory?.categoryId) {
      setSelectedCategory(parseInt(paramsCategory.categoryId));
    }
    // Si hay un ID de producto en los parámetros de consulta
    else if (productIdFromQuery) {
      setUrlProductId(parseInt(productIdFromQuery));
    }

    // Simular tiempo de carga inicial
    const timer = setTimeout(() => setIsInitialLoading(false), 500);
    return () => clearTimeout(timer);
  }, [matchCategory, matchSubcategory, matchProduct, paramsCategory, paramsSubcategory, paramsProduct]);

  // Seleccionar el producto cuando los datos estén listos
  useEffect(() => {
    // Usar allProducts en lugar de products
    if (!urlProductId || !allProducts || allProducts.length === 0) return;

    console.log("Looking for product with ID:", urlProductId, "in", allProducts.length, "products");

    const product = allProducts.find(p => p.id === urlProductId);
    if (product) {
      console.log("Found product:", product.name);

      // Establecer el producto seleccionado para abrir el modal
      setSelectedProduct(product);

      // Resetear el índice de imagen
      setCurrentImageIndex(0);

      // Si tenemos categorías disponibles, intentar establecer la jerarquía correcta
      if (categories && categories.length > 0) {
        // Buscar categoría y subcategoría correspondientes
        let foundCategory = false;
        for (const category of categories) {
          if (category.children) {
            const subcategory = category.children.find(sub => sub.id === product.categoryId);
            if (subcategory) {
              setSelectedCategory(category.id);
              setSelectedSubcategory(subcategory.id);
              foundCategory = true;
              console.log("Found category hierarchy for product:", category.name, "->", subcategory.name);
              break;
            }
          }
        }

        // Si no se encontró la categoría/subcategoría, usar el categoryId del producto como fallback
        if (!foundCategory && product.categoryId) {
          // Intentar encontrar si el categoryId es una categoría principal
          const isMainCategory = categories.some(cat => cat.id === product.categoryId);

          if (isMainCategory) {
            setSelectedCategory(product.categoryId);
            setSelectedSubcategory(null);
            console.log("Using product's categoryId as main category:", product.categoryId);
          } else {
            // Asumir que es una subcategoría, pero no sabemos de qué categoría principal
            // Establecer una categoría por defecto (la primera)
            if (categories.length > 0) {
              setSelectedCategory(categories[0].id);
              setSelectedSubcategory(product.categoryId);
              console.log("Using first category as parent for subcategory:", categories[0].id, "->", product.categoryId);
            }
          }
        }
      }
    } else {
      console.log(`No se encontró el producto con ID ${urlProductId}`);
    }
  }, [urlProductId, allProducts, categories]); // Depender de allProducts

  useEffect(() => {
    if (categoriesError) {
      console.error("Error loading categories:", categoriesError);
    }
    if (categories) {
      console.log("Categories loaded successfully:", JSON.stringify(categories, null, 2));
    }
  }, [categories, categoriesError]);

  // Actualizar la URL cuando cambia la selección de categoría, subcategoría o producto
  useEffect(() => {
    // Solo actualizar la URL si tenemos los datos necesarios
    if (!selectedCategory) return;

    // Si tenemos un producto seleccionado, actualizar la URL para reflejarlo
    if (selectedProduct && selectedCategory && selectedSubcategory) {
      const slug = selectedProduct.name
        .toLowerCase()
        .replace(/[^\w\sáéíóúüñ]/gi, '')
        .replace(/\s+/g, '-')
        .normalize("NFD").replace(/[\u0300-\u036f]/g, '');

      const newUrl = `/new/category/${selectedCategory}/subcategory/${selectedSubcategory}/product/${selectedProduct.id}-${slug}`;

      // Actualizar la URL sin recargar la página
      window.history.pushState({}, '', newUrl);
    }
    // Si solo tenemos categoría y subcategoría, actualizar la URL para reflejar eso
    else if (selectedCategory && selectedSubcategory) {
      const newUrl = `/new/category/${selectedCategory}/subcategory/${selectedSubcategory}`;
      window.history.pushState({}, '', newUrl);
    }
    // Si solo tenemos categoría, actualizar la URL para reflejar eso
    else if (selectedCategory) {
      const newUrl = `/new/category/${selectedCategory}`;
      window.history.pushState({}, '', newUrl);
    }
  }, [selectedCategory, selectedSubcategory, selectedProduct]);

  // Manejar la navegación del usuario (botones atrás/adelante)
  useEffect(() => {
    const handlePopState = () => {
      // Extraer información de la URL actual
      const path = window.location.pathname;

      // Comprobar si la URL contiene un producto
      const productMatch = path.match(/\/product\/(\d+)-/);
      if (productMatch) {
        const productId = parseInt(productMatch[1]);
        setUrlProductId(productId);
      } else {
        // Si no hay producto en la URL, cerrar el modal de producto
        setSelectedProduct(null);
        setUrlProductId(null);
      }

      // Comprobar si la URL contiene una subcategoría
      const subcategoryMatch = path.match(/\/subcategory\/(\d+)/);
      if (subcategoryMatch) {
        const subcategoryId = parseInt(subcategoryMatch[1]);
        setSelectedSubcategory(subcategoryId);
      } else {
        setSelectedSubcategory(null);
      }

      // Comprobar si la URL contiene una categoría
      const categoryMatch = path.match(/\/category\/(\d+)/);
      if (categoryMatch) {
        const categoryId = parseInt(categoryMatch[1]);
        setSelectedCategory(categoryId);
      } else {
        setSelectedCategory(null);
      }
    };

    // Añadir event listener para popstate (cuando el usuario usa los botones atrás/adelante)
    window.addEventListener('popstate', handlePopState);

    // Limpiar el event listener al desmontar
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Detectar producto en la URL al cargar la página
  useEffect(() => {
    // Extraer información de la URL actual
    const path = window.location.pathname;

    // Comprobar si la URL contiene un producto
    const productMatch = path.match(/\/product\/(\d+)-/);
    if (productMatch) {
      const productId = parseInt(productMatch[1]);
      console.log("Product ID detected in URL on initial load:", productId);
      setUrlProductId(productId);

      // Extraer categoría y subcategoría de la URL
      const categoryMatch = path.match(/\/category\/(\d+)/);
      const subcategoryMatch = path.match(/\/subcategory\/(\d+)/);

      if (categoryMatch && subcategoryMatch) {
        const categoryId = parseInt(categoryMatch[1]);
        const subcategoryId = parseInt(subcategoryMatch[1]);

        setSelectedCategory(categoryId);
        setSelectedSubcategory(subcategoryId);
        console.log("Categoría y subcategoría establecidas desde URL:", categoryId, subcategoryId);

        // Forzar la carga de productos para esta subcategoría
        const loadProductData = async () => {
          try {
            console.log("Cargando productos para subcategoría:", subcategoryId);
            const response = await fetch(`https://jlufgkgssnxumguvknhc.supabase.co/functions/v1/marketplace/products/condition/new/category/${subcategoryId}`);
            if (!response.ok) {
              throw new Error("Error al cargar los productos");
            }
            const data = await response.json();
            console.log("Productos cargados directamente:", data.length);

            // Buscar el producto específico
            const product = data.find((p: { id: number }) => p.id === productId);
            if (product) {
              console.log("Producto encontrado directamente:", product);

              // Establecer el producto seleccionado para abrir el modal
              setSelectedProduct(product);

              // Resetear el índice de imagen
              setCurrentImageIndex(0);
            } else {
              console.log("No se encontró el producto con ID:", productId);
            }
          } catch (error) {
            console.error("Error cargando productos:", error);
          }
        };

        loadProductData();
      }
    }
  }, []);

  // Find selected category and subcategory data
  const selectedCategoryData = useMemo(() => {
    if (!categories?.length) return null;
    const category = categories.find((cat: CategoryWithChildren) => cat.id === selectedCategory);
    console.log("Selected category:", category);
    return category;
  }, [categories, selectedCategory]);

  const selectedSubcategoryData = useMemo(() => {
    if (!selectedCategoryData?.children) return null;
    const subcategory = selectedCategoryData.children.find(
      (sub: CategoryWithChildren) => sub.id === selectedSubcategory
    );
    console.log("Selected subcategory:", subcategory);
    return subcategory;
  }, [selectedCategoryData, selectedSubcategory]);

  // Generate unique tags for filters from the complete hierarchy
  const availableTags = useMemo(() => {
    if (!categories?.length) return [];

    // Crear un conjunto para almacenar las etiquetas únicas
    const tags = new Set<string>();

    // Añadir solo las categorías principales y subcategorías más relevantes
    categories.forEach((category) => {
      // Añadir la categoría completa en lugar de palabras individuales
      tags.add(category.name.toLowerCase());

      // Añadir subcategorías
      category.children?.forEach((sub) => tags.add(sub.name.toLowerCase()));
    });

    return Array.from(tags)
      .filter((tag) => tag.length > 2) // Permitir etiquetas un poco más cortas
      .slice(0, 8); // Mostrar menos tags
  }, [categories]);

  // Filtrar productos basado en la selección actual
  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];

    let productsToShow = [...allProducts];

    // Filtrar por categoría seleccionada
    if (selectedCategory) {
      const categoryData = categories?.find(cat => cat.id === selectedCategory);
      if (categoryData) {
        const subcategoryIds = categoryData.children?.map(sub => sub.id) || [];
        // Incluir productos de la categoría principal (si aplica) y sus subcategorías
        const validCategoryIds = [selectedCategory, ...subcategoryIds];
        productsToShow = productsToShow.filter(product =>
          product.categoryId && validCategoryIds.includes(product.categoryId)
        );
      }
    }

    // Filtrar por subcategoría seleccionada (refina la selección de categoría)
    if (selectedSubcategory) {
      productsToShow = productsToShow.filter(product => product.categoryId === selectedSubcategory);
    }

    // Filtrar por búsqueda de texto
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      productsToShow = productsToShow.filter(product =>
        product.name.toLowerCase().includes(lowerCaseQuery) ||
        product.description.toLowerCase().includes(lowerCaseQuery) ||
        (product.brand || "").toLowerCase().includes(lowerCaseQuery)
        // Podrías añadir búsqueda en features o specs si es necesario
      );
    }

     // Filtrar por tags seleccionados (EXPERIMENTAL)
     if (selectedFilters.length > 0) {
        productsToShow = productsToShow.filter(product => {
            // Buscar tags en nombre, marca, descripción, categoría/subcategoría (si se tiene la info)
            const productText = `${product.name} ${product.brand} ${product.description}`.toLowerCase();
            // Podríamos añadir nombres de categoría/subcategoría si los obtenemos
            return selectedFilters.some(filter => productText.includes(filter.toLowerCase()));
        });
    }

    return productsToShow;
  }, [allProducts, selectedCategory, selectedSubcategory, searchQuery, selectedFilters, categories]);

  // Search through the complete hierarchy including products (ajustado para buscar en filteredProducts)
  const searchResults = useMemo(() => {
    if (!searchQuery && !selectedFilters.length) return [];
    if (!categories?.length && !allProducts?.length) return [];

    const results: Array<{
      category?: CategoryWithChildren; // Usar CategoryWithChildren
      subcategory?: CategoryWithChildren;
      product?: Product;
      type: "category" | "subcategory" | "product";
    }> = [];

    const lowerCaseQuery = searchQuery.toLowerCase();
    const lowerCaseFilters = selectedFilters.map(f => f.toLowerCase());

    // Buscar en categorías y subcategorías
    categories?.forEach((category) => {
      const categoryNameLower = category.name.toLowerCase();
      const categoryDescLower = (category.description || "").toLowerCase();
      const categoryMatchesQuery = categoryNameLower.includes(lowerCaseQuery) || categoryDescLower.includes(lowerCaseQuery);
      const categoryMatchesFilter = !lowerCaseFilters.length || lowerCaseFilters.some(filter => categoryNameLower.includes(filter));

      if (categoryMatchesQuery && categoryMatchesFilter) {
        results.push({ category, type: "category" });
      }

      category.children?.forEach((subcategory) => {
        const subNameLower = subcategory.name.toLowerCase();
        const subDescLower = (subcategory.description || "").toLowerCase();
        const subcategoryMatchesQuery = subNameLower.includes(lowerCaseQuery) || subDescLower.includes(lowerCaseQuery);
        const subcategoryMatchesFilter = !lowerCaseFilters.length || lowerCaseFilters.some(filter => subNameLower.includes(filter));

        // Mostrar subcategoría si coincide directamente o si su categoría padre coincide con el filtro
        if ((subcategoryMatchesQuery || (categoryMatchesQuery && !searchQuery)) && subcategoryMatchesFilter) {
          results.push({ category, subcategory, type: "subcategory" });
        }
      });
    });

    // Buscar en productos (usar filteredProducts para la búsqueda de texto si es relevante)
    // O buscar en allProducts si queremos que la búsqueda sea independiente del filtro Categ/Subcateg
    const productsToSearch = allProducts || []; // Buscar siempre en todos los productos
    if (searchQuery || selectedFilters.length > 0) {
      productsToSearch.forEach((product) => {
        const productNameLower = product.name.toLowerCase();
        const productDescLower = product.description.toLowerCase();
        const productBrandLower = (product.brand || "").toLowerCase();

        const productMatchesQuery =
          productNameLower.includes(lowerCaseQuery) ||
          productDescLower.includes(lowerCaseQuery) ||
          productBrandLower.includes(lowerCaseQuery);

        const productMatchesFilters = !lowerCaseFilters.length ||
            lowerCaseFilters.some(filter =>
                productNameLower.includes(filter) ||
                productBrandLower.includes(filter)
            );

        if (productMatchesQuery && productMatchesFilters) {
            // Encontrar la categoría/subcategoría del producto para el resultado
            let productCat: CategoryWithChildren | undefined;
            let productSubcat: CategoryWithChildren | undefined;
            if (product.categoryId && categories) {
                for (const cat of categories) {
                    if (cat.id === product.categoryId) {
                        productCat = cat;
                        break;
                    }
                    const sub = cat.children?.find(s => s.id === product.categoryId);
                    if (sub) {
                        productCat = cat;
                        productSubcat = sub;
                        break;
                    }
                }
            }
          results.push({ product, category: productCat, subcategory: productSubcat, type: "product" });
        }
      });
    }

    // Limitar resultados y priorizar coincidencias
    return results.slice(0, 10); // Limitar a 10 resultados

  }, [searchQuery, selectedFilters, categories, allProducts]); // Depender de allProducts

  const handleFilterToggle = (tag: string) => {
    setSelectedFilters((prev) =>
      prev.includes(tag) ? prev.filter((f) => f !== tag) : [...prev, tag]
    );
  };

  const handleCategorySelect = (categoryId: number | null) => { // Permitir null para deseleccionar
    // Si es la misma categoría, deseleccionar todo
    if (selectedCategory === categoryId || categoryId === null) {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setLocation("/new");
    } else {
      setSelectedCategory(categoryId);
      setSelectedSubcategory(null); // Reset subcategory when changing category
      setLocation(`/new/category/${categoryId}`);
    }
  };

  const handleSubcategorySelect = (subcategoryId: number | null) => { // Permitir null para deseleccionar
     // Si es la misma subcategoría, deseleccionar solo la subcategoría
    if (selectedSubcategory === subcategoryId || subcategoryId === null) {
      setSelectedSubcategory(null);
      // Mantener la URL de la categoría padre
      if (selectedCategory) {
        setLocation(`/new/category/${selectedCategory}`);
      } else {
         setLocation("/new"); // Si no hay categoría padre, ir a la base
      }
    } else {
      // Asegurarse de que tenemos una categoría seleccionada
      if (selectedCategory) {
          setSelectedSubcategory(subcategoryId);
          setLocation(`/new/category/${selectedCategory}/subcategory/${subcategoryId}`);
      } else {
          // Encontrar la categoría padre de esta subcategoría y seleccionarla también
          const parentCategory = categories?.find(cat => cat.children?.some(sub => sub.id === subcategoryId));
          if (parentCategory) {
              setSelectedCategory(parentCategory.id);
              setSelectedSubcategory(subcategoryId);
              setLocation(`/new/category/${parentCategory.id}/subcategory/${subcategoryId}`);
          } else {
              console.warn("No se pudo encontrar la categoría padre para la subcategoría:", subcategoryId);
              // Opcional: seleccionar solo la subcategoría si no se encuentra padre?
              // setSelectedSubcategory(subcategoryId);
          }
      }
    }
  };

  // Función para limpiar todos los filtros (categoría y subcategoría)
  const clearFilters = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setLocation("/new");
  };

  const handleBack = () => {
    if (selectedSubcategory) {
      setSelectedSubcategory(null);
    } else {
      setSelectedCategory(null);
    }
  };

  // Función para manejar errores de carga de imágenes
  const handleImageError = (imageUrl: string, e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    try {
      // Marcar la imagen como rota
      setBrokenImages(prev => ({ ...prev, [imageUrl]: true }));

      // Extraer el nombre del archivo de la URL
      let fileName = "";

      if (imageUrl.includes('/')) {
        // Obtener el nombre del archivo de la ruta
        const parts = imageUrl.split('/');
        fileName = parts[parts.length - 1];
      } else {
        fileName = imageUrl;
      }

      // Intentar con la ruta directa
      const newSrc = `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/products/${fileName}`;

      // Establecer la nueva URL de forma segura
      const imgElement = e.currentTarget;
      if (imgElement) {
        imgElement.src = newSrc;

        // Añadir un segundo manejador de error como respaldo de forma segura
        imgElement.onerror = () => {
          // Si la segunda URL también falla, usar el placeholder
          setBrokenImages(prev => ({ ...prev, [newSrc]: true }));
          if (imgElement) {
            imgElement.src = "/placeholder-image.png";
            imgElement.onerror = null; // Evitar bucles infinitos
          }
        };
      }
    } catch (error) {
      // En caso de error, usar el placeholder
      const imgElement = e.currentTarget;
      if (imgElement) {
        imgElement.src = "/placeholder-image.png";
      }
      // Marcar la imagen como rota
      setBrokenImages(prev => ({ ...prev, [imageUrl]: true }));
    }
  };

  // Función para generar la URL del producto
  const generateProductUrl = (product: Product) => {
    // Crear el slug del producto a partir del nombre
    const slug = product.name
      .toLowerCase()
      .replace(/[^\w\sáéíóúüñ]/gi, '')  // Eliminar caracteres especiales pero mantener acentos y ñ
      .replace(/\s+/g, '-')     // Reemplazar espacios con guiones
      .normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // Eliminar acentos

    // Construir la URL completa usando la nueva ruta
    const baseUrl = window.location.origin;

    // Inicializar IDs
    let categoryId = 0;
    let subcategoryId = 0;

    // Verificar si tenemos el producto y su categoryId
    if (!product || !product.categoryId) {
      console.log("Product missing or has no categoryId:", product);
      return `${baseUrl}/new/category/0/subcategory/0/product/${product.id}-${slug}`;
    }

    // Verificar si tenemos categorías cargadas
    if (!categories || categories.length === 0) {
      console.log("No categories available");
      return `${baseUrl}/new/category/0/subcategory/0/product/${product.id}-${slug}`;
    }

    // Obtener el ID de categoría del producto
    const productCategoryId = product.categoryId;
    console.log("Product categoryId:", productCategoryId);

    // 1. Verificar si el categoryId es una categoría principal
    const isMainCategory = categories.some(cat => cat.id === productCategoryId);

    if (isMainCategory) {
      // Es una categoría principal
      categoryId = productCategoryId;
      subcategoryId = 0;
      console.log("Found as main category. categoryId:", categoryId);
    } else {
      // 2. Verificar si es una subcategoría
      let foundAsSubcategory = false;

      // Buscar en todas las categorías principales
      for (const mainCategory of categories) {
        if (!mainCategory.children) continue;

        // Buscar en las subcategorías de esta categoría principal
        for (const subCategory of mainCategory.children) {
          if (subCategory.id === productCategoryId) {
            // Encontramos la subcategoría
            categoryId = mainCategory.id;
            subcategoryId = productCategoryId;
            foundAsSubcategory = true;
            console.log("Found as subcategory. Main categoryId:", categoryId, "subcategoryId:", subcategoryId);
            break;
          }
        }

        if (foundAsSubcategory) break;
      }

      // Si no se encontró como subcategoría, usar valores por defecto
      if (!foundAsSubcategory) {
        console.log("Category not found in hierarchy, using default values");
        return `${baseUrl}/new/category/0/subcategory/0/product/${product.id}-${slug}`;
      }
    }

    // Construir la URL con los IDs correctos
    const url = `${baseUrl}/new/category/${categoryId}/subcategory/${subcategoryId}/product/${product.id}-${slug}`;
    console.log("Generated URL:", url);
    return url;
  };

  // Render loading state for initial data (categories and products)
  if (categoriesLoading || allProductsLoading) { // Comprobar ambos loadings
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Render error state (priorizar error de categorías)
  if (categoriesError || productsError) { // Comprobar ambos errores
    const error = categoriesError || productsError;
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="text-red-500 text-xl">Error al cargar datos</div>
          <div className="text-white/60 text-sm">{(error as Error).message}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#FCAA0D] text-white rounded-lg hover:bg-[#FCAA0D]/90 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Render empty state if no categories
  if (!categories?.length) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 text-[#FCAA0D] animate-spin" />
          <h2 className="text-white text-xl font-medium">Cargando categorías...</h2>
        </div>
      </div>
    );
  }

  // Vista de productos - Ahora recibe los productos filtrados
  const renderProductView = (productsToRender: Product[]) => (
    <motion.div
      // Quitar layoutId si ya no se anima como un reemplazo de vista
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }} // Puede que no sea necesario si siempre está visible
      transition={{ duration: 0.3 }}
      className="bg-zinc-900/50 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl border border-zinc-800/50 mt-8"
    >
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <motion.h2
            className="text-xl md:text-2xl font-bold text-white"
            // layoutId={`product-title-${selectedSubcategory}`} // Quitar si causa problemas
          >
            {selectedSubcategoryData?.name || selectedCategoryData?.name || 'Todos los Productos'}
          </motion.h2>
        {productsToRender && productsToRender.length > 0 && (
          <span className="text-zinc-500 text-sm">
            {productsToRender.length} {productsToRender.length === 1 ? 'producto encontrado' : 'productos encontrados'}
          </span>
        )}
      </div>

      {allProductsLoading ? ( // Usar allProductsLoading aquí
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : productsToRender && productsToRender.length > 0 ? (
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }} // Reducir delay si es necesario
        >
          {productsToRender.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer h-full"
              onClick={() => handleProductSelect(product)}
            >
              <div className="relative overflow-hidden bg-black rounded-xl border border-zinc-800/50 group-hover:border-[#FCAA0D]/30 group-hover:shadow-[0_0_30px_rgba(252,170,13,0.2)] transition-all duration-300 h-full">
                {/* Imagen principal destacada - Ahora aún más dominante */}
                <div className="w-full aspect-[3/4] overflow-hidden relative">
                  {(() => {
                    // Verificar si hay imágenes disponibles
                    if (product.images &&
                        Array.isArray(product.images) &&
                        product.images.length > 0 &&
                        product.images[0] &&
                        isValidImageUrl(product.images[0]) &&
                        !brokenImages[product.images[0]]) {
                      // Guardar la primera imagen en una constante para evitar errores de undefined
                      const firstImage = product.images[0];
                      return (
                        <>
                          {loadingImages[firstImage] && (
                            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-10">
                              <LoadingSpinner />
                            </div>
                          )}
                          <img
                            src={firstImage}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            onError={(e) => handleImageError(firstImage, e)}
                            onLoad={() => setLoadingImages(prev => ({ ...prev, [firstImage]: false }))}
                            onLoadStart={() => setLoadingImages(prev => ({ ...prev, [firstImage]: true }))}
                          />
                        </>
                      );
                    }
                    // Si no hay imágenes, verificar si hay una URL de imagen
                    else if (product.imageUrl &&
                             isValidImageUrl(product.imageUrl) &&
                             !brokenImages[product.imageUrl]) {
                      // Guardar la URL de la imagen en una constante para evitar errores de undefined
                      const imageUrl = product.imageUrl;
                      return (
                        <>
                          {loadingImages[imageUrl] && (
                            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900/80 z-10">
                              <LoadingSpinner />
                            </div>
                          )}
                          <img
                            src={imageUrl}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            onError={(e) => handleImageError(imageUrl, e)}
                            onLoad={() => setLoadingImages(prev => ({ ...prev, [imageUrl]: false }))}
                            onLoadStart={() => setLoadingImages(prev => ({ ...prev, [imageUrl]: true }))}
                          />
                        </>
                      );
                    }
                    // Si no hay imágenes disponibles, mostrar un placeholder
                    else {
                      return (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-800/80">
                          <FileText className="w-24 h-24 text-zinc-600" />
                        </div>
                      );
                    }
                  })()}

                  {/* Overlay con degradado minimalista */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-70 group-hover:opacity-50 transition-opacity duration-300"></div>

                  {/* Etiqueta flotante de marca */}
                  {product.brand && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm border border-white/10 opacity-100 transition-all duration-300">
                      {product.brand}
                    </div>
                  )}

                  {/* Título del producto siempre visible */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                    <h3 className="text-lg font-bold text-white group-hover:text-[#FCAA0D] transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                  </div>

                  {/* Panel de información que aparece con hover */}
                  <div className="absolute inset-0 flex flex-col justify-end bg-black/80 backdrop-blur-sm p-4 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out z-20">
                    {/* Título en el panel de hover */}
                    <h3 className="text-lg font-bold text-[#FCAA0D] mb-3">
                      {product.name}
                    </h3>

                    {/* Descripción del producto */}
                    <div className="mb-3">
                      <div className="text-xs text-zinc-400 uppercase mb-1">Descripción</div>
                      <p className="text-zinc-200 text-sm line-clamp-3">
                        {product.description}
                      </p>
                    </div>

                    {/* Características principales (si existen) */}
                    {product.features && product.features.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-zinc-400 uppercase mb-1">Características</div>
                        <ul className="text-xs text-white space-y-1">
                          {product.features.slice(0, 2).map((feature, i) => (
                            <li key={i} className="flex items-start">
                              <div className="w-1 h-1 rounded-full bg-[#FCAA0D] mr-2 mt-1.5 flex-shrink-0"></div>
                              <span className="line-clamp-1">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Botón para ver más */}
                    <div className="flex justify-end mt-auto pt-2">
                      <div className="h-8 w-8 rounded-full bg-[#FCAA0D] flex items-center justify-center transition-transform duration-300 transform hover:scale-110">
                        <ArrowRight className="w-4 h-4 text-black" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      ) : (
        <div className="text-center py-12">
          <p className="text-zinc-500">
            {searchQuery || selectedFilters.length > 0
                ? 'No se encontraron productos que coincidan con tu búsqueda o filtros.'
                : 'No hay productos disponibles en esta selección.'}
            </p>
        </div>
      )}
    </motion.div>
  );

  // Vista del modal de producto
  const renderProductModal = () => {
    if (!selectedProduct) return null;

    // Debug completo del objeto producto
    console.log("Objeto producto completo:", JSON.stringify(selectedProduct, null, 2));

    // Crear array de items para el carrusel
    const carouselItems: Array<{ type: 'image' | 'video'; url: string }> = [];

    // Verificar manualmente el valor de videoUrl y video_url
    const videoUrl = selectedProduct.videoUrl || (selectedProduct as any).video_url || null;
    console.log("Verificación manual de videoUrl:", videoUrl);
    console.log("Tipo de videoUrl:", typeof videoUrl);

    // Añadir video primero - usando videoUrl o video_url
    if (videoUrl) {
      console.log("Adding video to carousel items:", videoUrl);
      carouselItems.push({ type: 'video', url: videoUrl });
    } else {
      console.log("No video URL found in any format");

      // Verificar si está en otra propiedad o formato
      Object.keys(selectedProduct).forEach(key => {
        if (key.toLowerCase().includes('video') && (selectedProduct as any)[key]) {
          console.log(`Found video in property ${key}:`, (selectedProduct as any)[key]);
          const foundUrl = (selectedProduct as any)[key];
          if (typeof foundUrl === 'string' && foundUrl.trim() !== '') {
            console.log("Using found video URL:", foundUrl);
            carouselItems.push({ type: 'video', url: foundUrl });
          }
        }
      });
    }

    // Añadir imágenes válidas
    if (selectedProduct.images && selectedProduct.images.length > 0) {
      console.log("Processing product images:", selectedProduct.images.length, "images found");
      selectedProduct.images.forEach((imgUrl, index) => {
        if (isValidImageUrl(imgUrl)) {
          console.log(`Image ${index} is valid:`, imgUrl);
          carouselItems.push({ type: 'image', url: imgUrl });
        } else {
          console.log(`Image ${index} is invalid:`, imgUrl);
        }
      });
    } else {
      console.log("No images found in product data");
    }

    // Si no hay items, verificar si podemos usar la URL truncada que mencionó el usuario
    if (carouselItems.length === 0 && selectedProduct.id === 174) {
      const manualVideoUrl = "https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/product-videos/public/174370";
      console.log("Usando URL manual para el producto AHORASI:", manualVideoUrl);
      carouselItems.push({ type: 'video', url: manualVideoUrl });
    }

    console.log("Final carousel items:", carouselItems.length, "items");
    carouselItems.forEach((item, i) => console.log(`  Item ${i}: ${item.type} => ${item.url}`));

    // Combinar características y especificaciones
    const features = selectedProduct.features || [];
    const specs = selectedProduct.technicalSpecs || [];

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black z-[9999] flex"
      >
        {/* Área principal para la visualización de imágenes a pantalla completa */}
        <div className="relative flex-1 h-full overflow-hidden flex items-center justify-center">
          <ProductImageCarousel
            items={carouselItems}
            productName={selectedProduct.name}
            onClose={() => handleCloseProductModal()}
          />

          {/* Botones de control flotantes en la esquina superior */}
          <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
            {/* Botón para mostrar/ocultar panel de información */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowInfoPanel(!showInfoPanel);
              }}
              className="p-3 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
              aria-label="Información del producto"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Botón de cierre */}
            <button
              onClick={() => handleCloseProductModal()}
              className="p-3 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Título flotante en la parte inferior */}
          <div className="absolute bottom-6 left-6 z-40 max-w-2xl">
            <div className="bg-black/70 backdrop-blur-md p-4 rounded-xl shadow-lg">
              <div className="flex items-center gap-3 mb-1">
                <span className="px-3 py-1 rounded-full bg-[#FCAA0D]/20 text-[#FCAA0D] text-sm font-medium">
                  {selectedProduct.brand}
                </span>
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-500 text-sm font-medium">
                  Disponible
                </span>
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">
                {selectedProduct.name}
              </h2>
            </div>
          </div>
        </div>

        {/* Panel lateral de información y controles (deslizable) */}
        <AnimatePresence>
          {showInfoPanel && (
            <motion.div
              initial={{ x: '100%', opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-[400px] h-full bg-zinc-900 border-l border-zinc-800 overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent"
            >
              <div className="p-6 space-y-6">
                {/* Encabezado con información clave */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">Detalles del Producto</h3>
                    <button
                      onClick={() => setShowInfoPanel(false)}
                      className="p-2 rounded-full hover:bg-zinc-800"
                    >
                      <X className="h-5 w-5 text-zinc-400" />
                    </button>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-2xl text-[#FCAA0D] font-bold">
                      {isUserLoggedIn ? selectedProduct.price : '---'}
                    </span>
                  </div>

                  {!isUserLoggedIn && (
                    <div className="bg-zinc-800/70 border border-zinc-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Zap className="w-5 h-5 text-[#FCAA0D]" />
                        <h4 className="text-white font-medium">Precio disponible para usuarios registrados</h4>
                      </div>
                      <p className="text-zinc-400 text-sm mb-3">
                        Para ver el precio de este producto, inicie sesión o regístrese gratuitamente.
                      </p>
                      <button
                        onClick={() => setShowAuthModal(true)}
                        className="bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-medium px-4 py-2 rounded-lg transition-colors text-sm w-full"
                      >
                        Iniciar sesión / Registrarse
                      </button>
                    </div>
                  )}

                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <p className="text-zinc-300">
                      {selectedProduct.description}
                    </p>
                  </div>
                </div>

                {/* Acciones principales */}
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      handleCloseProductModal();
                      setShowContactForm(true);
                    }}
                    className="w-full py-3 bg-[#FCAA0D] hover:bg-[#FCAA0D]/90 text-black font-semibold rounded-lg flex items-center justify-center gap-2"
                  >
                    <Mail className="h-5 w-5" />
                    Contactar
                  </button>

                  <button
                    onClick={() => {
                      handleCloseProductModal();
                      setShowScheduleCallForm(true);
                    }}
                    className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2"
                  >
                    <Phone className="h-5 w-5" />
                    Agendar llamada
                  </button>
                </div>

                {/* Características y Especificaciones */}
                <div className="bg-zinc-800/50 rounded-xl p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#FCAA0D]/10">
                      <ListChecks className="w-5 h-5 text-[#FCAA0D]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Características y especificaciones
                    </h3>
                  </div>

                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                    {features.map((feature, index) => (
                      <div key={`feature-${index}`} className="flex items-center gap-2 text-zinc-300">
                        <CheckCircle2 className="w-4 h-4 text-[#FCAA0D] shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}

                    {features.length > 0 && specs.length > 0 && (
                      <div className="h-px bg-zinc-700/30 my-4"></div>
                    )}

                    {specs.map((spec, index) => (
                      <div key={`spec-${index}`} className="flex justify-between text-sm border-b border-zinc-700/50 pb-2">
                        <span className="text-zinc-400 font-medium">{spec.label}</span>
                        <span className="text-white">{spec.value}</span>
                      </div>
                    ))}

                    {features.length === 0 && specs.length === 0 && (
                      <div className="text-zinc-500 text-sm italic">
                        No hay características o especificaciones disponibles
                      </div>
                    )}
                  </div>
                </div>

                {/* Simulador Financiero */}
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <button
                    onClick={() => {
                      if (isUserLoggedIn) {
                        console.log("Click en Simular financiación", selectedProduct?.id);
                        setShowFinancialSimulator(true);
                        // No cerramos inmediatamente el modal para que no se pierda la referencia al producto
                        // handleCloseProductModal();
                      } else {
                        setShowAuthModal(true);
                      }
                    }}
                    className="w-full flex items-center justify-between hover:bg-zinc-800 transition-colors rounded-lg p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <Calculator className="w-4 h-4 text-emerald-500" />
                      </div>
                      <span className="text-white font-medium">Simular financiación</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>

                {/* Compartir */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => shareOnWhatsApp(selectedProduct)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600/20 text-green-500 hover:bg-green-600/30 transition-colors"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <span>WhatsApp</span>
                  </button>

                  <button
                    onClick={() => copyProductLink(selectedProduct)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
                  >
                    {isLinkCopied ? (
                      <>
                        <Check className="h-5 w-5 text-emerald-500" />
                        <span className="text-emerald-500">¡Copiado!</span>
                      </>
                    ) : (
                      <>
                        <Link className="h-5 w-5" />
                        <span>Copiar enlace</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  // Vista del formulario de agendar llamada
  const renderScheduleCallForm = () => {
    if (!selectedProduct || !showScheduleCallForm) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={() => setShowScheduleCallForm(false)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-zinc-900 rounded-2xl max-w-lg w-full"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                Consultar Precio - {selectedProduct.name}
              </h3>
              <button
                onClick={() => setShowScheduleCallForm(false)}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <ScheduleCallForm
              onSuccess={() => setShowScheduleCallForm(false)}
              showHeader={false}
              className="bg-transparent p-0"
            />
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Vista del formulario de contacto (mantenido para compatibilidad)
  const renderContactForm = () => {
    if (!selectedProduct || !showContactForm) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={() => setShowContactForm(false)}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-zinc-900 rounded-2xl max-w-lg w-full"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                Solicitar Información
              </h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-zinc-400 mb-4">
              Utilice el formulario de agendar llamada para consultar sobre este producto.
            </p>

            <button
              onClick={() => {
                setShowContactForm(false);
                setShowScheduleCallForm(true);
              }}
              className="w-full bg-[#FCAA0D] text-zinc-900 font-bold px-6 py-3 rounded-xl hover:bg-[#FCAA0D]/90 transition-colors flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5 text-zinc-900" />
              Agendar Llamada
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Función para compartir en WhatsApp
  const shareOnWhatsApp = (product: Product) => {
    if (!product) return;

    const productUrl = generateProductUrl(product);
    const message = `¡Mira este producto en Innotec! ${product.name}: ${productUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

    // Abrir WhatsApp en una nueva pestaña
    window.open(whatsappUrl, '_blank');
  };

  // Función para copiar el enlace del producto
  const copyProductLink = (product: Product) => {
    if (!product) return;

    console.log("Copying link for product:", product);
    console.log("Current selected category:", selectedCategory);
    console.log("Current selected subcategory:", selectedSubcategory);

    // Asegurarnos de que el producto tenga la información de categoría correcta
    // Si el producto no tiene categoryId pero tenemos una subcategoría seleccionada, usarla
    if (!product.categoryId && selectedSubcategory) {
      console.log("Product has no categoryId, using selected subcategory:", selectedSubcategory);
      product = {
        ...product,
        categoryId: selectedSubcategory
      };
    }

    // Si estamos en una vista de categoría/subcategoría específica, asegurarnos de que
    // el producto tenga la información correcta de jerarquía
    if (selectedCategory && selectedSubcategory) {
      console.log("Using current navigation context for URL generation");

      // Construir la URL manualmente usando la navegación actual
      const baseUrl = window.location.origin;
      const slug = product.name
        .toLowerCase()
        .replace(/[^\w\sáéíóúüñ]/gi, '')
        .replace(/\s+/g, '-')
        .normalize("NFD").replace(/[\u0300-\u036f]/g, '');

      const url = `${baseUrl}/new/category/${selectedCategory}/subcategory/${selectedSubcategory}/product/${product.id}-${slug}`;

      // Copiar al portapapeles
      navigator.clipboard.writeText(url)
        .then(() => {
          // Mostrar confirmación
          setIsLinkCopied(true);

          // Mostrar notificación toast
          toast({
            title: "Enlace copiado",
            description: "El enlace ha sido copiado al portapapeles",
            variant: "default",
          });

          // Restablecer después de 2 segundos
          setTimeout(() => {
            setIsLinkCopied(false);
          }, 2000);
        })
        .catch((err) => {
          console.error('Error al copiar el enlace:', err);
          toast({
            title: "Error al copiar",
            description: "No se pudo copiar el enlace al portapapeles.",
            variant: "destructive",
          });
        });

      return;
    }

    // Si no tenemos contexto de navegación, usar la función generateProductUrl
    const productUrl = generateProductUrl(product);

    // Copiar al portapapeles
    navigator.clipboard.writeText(productUrl)
      .then(() => {
        // Mostrar confirmación
        setIsLinkCopied(true);

        // Mostrar notificación toast
        toast({
          title: "Enlace copiado",
          description: "El enlace ha sido copiado al portapapeles",
          variant: "default",
        });

        // Restablecer después de 2 segundos
        setTimeout(() => {
          setIsLinkCopied(false);
        }, 2000);
      })
      .catch((err) => {
        console.error('Error al copiar el enlace:', err);
        toast({
          title: "Error al copiar",
          description: "No se pudo copiar el enlace al portapapeles.",
          variant: "destructive",
        });
      });
  };

  // Función para manejar la selección de un producto
  const handleProductSelect = (product: Product) => {
    console.log("Seleccionando producto:", product.id, product.name);
    console.log("Video URL en producto seleccionado:", product.videoUrl);

    // Caso especial para el producto AHORASI (ID: 174)
    if (product.id === 174) {
      console.log("Aplicando solución especial para producto AHORASI");

      // Completar la URL del video si está truncada
      if ((product.videoUrl || '').includes('174370') &&
          !(product.videoUrl || '').includes('.mov')) {
        const fullUrl = product.videoUrl + "5548725_n0ytcn6clf.mov";
        console.log("Completando URL del video para AHORASI:", fullUrl);
        product = {
          ...product,
          videoUrl: fullUrl
        };
      }
      // Si no tiene videoUrl, asignar uno manualmente
      else if (!product.videoUrl) {
        const manualUrl = "https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/product-videos/public/1743705548725_n0ytcn6clf.mov";
        console.log("Asignando URL manual para AHORASI:", manualUrl);
        product = {
          ...product,
          videoUrl: manualUrl
        };
      }
    }

    // Verificar si hay video_url en lugar de videoUrl
    if ((product as any).video_url && !product.videoUrl) {
      console.log("El producto tiene video_url pero no videoUrl, corrigiendo...");
      product = {
        ...product,
        videoUrl: (product as any).video_url
      };
    }

    setSelectedProduct(product);

    // Actualizar la URL con la ruta completa del producto
    if (product.categoryId) {
      // Usar la función generateProductUrl para generar una URL consistente
      const url = generateProductUrl(product);
      // Extraer solo la ruta relativa de la URL completa
      const path = new URL(url).pathname;
      setLocation(path);
    }
  };

  // Función para cerrar el modal de producto
  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);

    // Actualizar la URL para eliminar el ID del producto
    if (selectedCategory && selectedSubcategory) {
      // Si hay categoría y subcategoría seleccionadas, navegar a la URL de la subcategoría
      setLocation(`/new/category/${selectedCategory}/subcategory/${selectedSubcategory}`);
    } else if (selectedCategory) {
      // Si solo hay categoría seleccionada, navegar a la URL de la categoría
      setLocation(`/new/category/${selectedCategory}`);
    } else {
      // Si no hay categoría ni subcategoría seleccionadas, navegar a la página principal
      setLocation('/new');
    }
  };

  // Renderizar modal de aviso de inicio de sesión
  const renderAuthModal = () => {
    if (!showAuthModal) return null;

    return (
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccessfulAuth={() => {
          setIsUserLoggedIn(true);
          toast({
            title: "Autenticación exitosa",
            description: "Has iniciado sesión correctamente",
          });
        }}
      />
    );
  };

  return (
    <div className="container mx-auto pb-16 px-4 lg:px-8 min-h-screen">
      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Barra de búsqueda inteligente */}
        <div className="mb-12">
          <div className="relative">
            <motion.div
              className={`bg-zinc-800/50 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/5 transition-all duration-200
                ${isSearchFocused ? 'ring-2 ring-[#FCAA0D]/50' : ''}`}
              layout
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1">
                  <Search
                    className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    placeholder="Buscar máquinas, modelos, categorías..."
                    className="w-full bg-zinc-900/50 text-white placeholder-zinc-500 rounded-lg pl-10 pr-4 py-2 outline-none"
                  />
                </div>
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setSearchQuery('')}
                    className="p-2 text-zinc-400 hover:text-white transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </motion.button>
                )}
              </div>

              {/* Filtros tipo tag */}
              <div className="flex flex-wrap gap-2">
                {availableTags.map(tag => (
                  <motion.button
                    key={tag}
                    onClick={() => handleFilterToggle(tag)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                      ${selectedFilters.includes(tag)
                        ? 'bg-[#FCAA0D] text-white'
                        : 'bg-zinc-700/50 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {tag}
                  </motion.button>
                ))}
              </div>

              {/* Resultados en tiempo real */}
              <AnimatePresence>
                {(searchQuery || selectedFilters.length > 0) && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 border-t border-white/5 pt-4"
                  >
                    {searchResults.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {searchResults.map((result, index) => (
                          <motion.div
                            key={`${result.type}-${result.category?.id}-${result.subcategory?.id || ''}-${result.product?.id || ''}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.05 }}
                            className="group cursor-pointer"
                            onClick={() => {
                              if (result.type === 'category') {
                                handleCategorySelect(result.category!.id);
                              } else if (result.type === 'subcategory') {
                                handleCategorySelect(result.category!.id);
                                handleSubcategorySelect(result.subcategory!.id);
                              } else if (result.type === 'product') {
                                handleProductSelect(result.product!);
                              }
                              setSearchQuery('');
                              setSelectedFilters([]);
                            }}
                          >
                            <div className={`p-4 rounded-lg bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 ${result.category?.gradient || 'from-[#1a1a1a] to-[#2a2a2a]'} group-hover:shadow-lg transition-all`}>
                              <div className="flex items-start gap-3">
                                <div className="text-white/90 shrink-0">
                                  {(() => {
                                    if (result.type === 'product') {
                                      return <FileText size={24} className="text-zinc-400" />;
                                    } else {
                                      const Icon = getIconComponent(result.category!.name);
                                      return <Icon size={24} className="text-[#FCAA0D]" />;
                                    }
                                  })()}
                                </div>
                                <div>
                                  <h4 className="text-white font-medium">
                                    {result.type === 'product' ? result.product!.name : result.subcategory ? result.subcategory.name : result.category!.name}
                                  </h4>
                                  {result.type === 'subcategory' && (
                                    <p className="text-sm text-white/60">
                                      {result.category!.name}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center text-zinc-500"
                      >
                        No se encontraron resultados
                      </motion.p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>



        {/* Main Content Area: Filters + Products */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Columna de Filtros (Categorías/Subcategorías) */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24 space-y-6">
                <div className="bg-zinc-900/70 border border-zinc-800/70 rounded-xl p-5 shadow-lg backdrop-blur-sm">
                    <div className="flex justify-between items-center mb-5">
                        <h3 className="text-lg font-semibold text-white">Categorías</h3>
                        {(selectedCategory || selectedSubcategory) && (
                            <button
                                onClick={clearFilters}
                                className="text-xs text-[#FCAA0D] hover:text-white flex items-center gap-1 transition-colors duration-200"
                            >
                                <FilterX size={14} /> Limpiar Filtros
                            </button>
                        )}
                    </div>

                  {/* Lista de Categorías Principales */}
                  <div className="space-y-3">
                    {categories.map((category) => (
                      <div key={category.id} className="group/category">
                        <motion.button
                          onClick={() => handleCategorySelect(category.id)}
                          className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center justify-between group
                            ${selectedCategory === category.id
                              ? 'bg-[#FCAA0D]/20 border-l-2 border-[#FCAA0D]'
                              : 'hover:bg-zinc-800/80 border-l-2 border-transparent hover:border-zinc-600'}`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="flex items-center gap-3">
                            {(() => {
                              const Icon = getIconComponent(category.name);
                              return <Icon size={20} className={`${selectedCategory === category.id ? 'text-[#FCAA0D]' : 'text-zinc-400 group-hover:text-white'} transition-colors duration-300`} />;
                            })()}
                            <span className={`font-medium ${selectedCategory === category.id ? 'text-[#FCAA0D]' : 'text-white'} transition-colors duration-300`}>
                              {category.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-zinc-800 text-zinc-400 group-hover:bg-zinc-700 group-hover:text-zinc-200 transition-colors duration-300">
                              {allProducts.filter(p => p.categoryId === category.id || category.children?.some(sub => sub.id === p.categoryId)).length}
                            </span>
                            {category.children && category.children.length > 0 && (
                              <div className={`transition-transform duration-300 ${selectedCategory === category.id ? 'rotate-180' : ''}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${selectedCategory === category.id ? 'text-[#FCAA0D]' : 'text-zinc-500'}`}>
                                  <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                              </div>
                            )}
                          </div>
                        </motion.button>

                        {/* Subcategorías (si la categoría está seleccionada) */}
                        <AnimatePresence>
                          {selectedCategory === category.id && category.children && category.children.length > 0 && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="pl-6 pr-2 pt-3 space-y-2"
                            >
                              {category.children.map((subcategory) => (
                                <motion.button
                                  key={subcategory.id}
                                  onClick={() => handleSubcategorySelect(subcategory.id)}
                                  className={`w-full text-left px-4 py-2.5 rounded-md transition-all duration-300 flex items-center justify-between group text-sm
                                    ${selectedSubcategory === subcategory.id
                                      ? 'bg-zinc-800/90 text-[#FCAA0D] border-l-2 border-[#FCAA0D]/70'
                                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50 border-l-2 border-transparent hover:border-zinc-700'}`}
                                  whileHover={{ x: 2 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className={`w-1.5 h-1.5 rounded-full ${selectedSubcategory === subcategory.id ? 'bg-[#FCAA0D]' : 'bg-zinc-600 group-hover:bg-zinc-400'} transition-colors duration-300`}></div>
                                    <span>{subcategory.name}</span>
                                  </div>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700/80 text-zinc-400 group-hover:bg-zinc-600 group-hover:text-zinc-200 transition-colors duration-300">
                                    {allProducts.filter(p => p.categoryId === subcategory.id).length}
                                  </span>
                                </motion.button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>
            </div>
          </aside>

          {/* Columna de Productos */}
          <main className="lg:col-span-9">
            {/* La vista de productos ahora usa filteredProducts */}
            {renderProductView(filteredProducts)}
          </main>

        </div>

        {/* SECCIÓN ANTIGUA COMENTADA - REEMPLAZADA POR GRID LAYOUT */}
        {/*
        <LayoutGroup>
          <AnimatePresence mode="wait">
            {!selectedCategory ? (
              // Vista principal de categorías (AHORA EN EL ASIDE)
              <motion.div
                // ... props ...
              >
                {categories.map((category) => (
                  <motion.div
                    // ... props ...
                    onClick={() => handleCategorySelect(category.id)}
                  >
                   // ... contenido tarjeta categoría ...
                  </motion.div>
                ))}
              </motion.div>
            ) : !selectedSubcategory ? (
              // Vista de subcategorías (AHORA EN EL ASIDE)
              <motion.div
                // ... props ...
              >
                {selectedCategoryData?.children?.map((subcategory) => (
                  <motion.div
                    // ... props ...
                    onClick={() => handleSubcategorySelect(subcategory.id)}
                  >
                   // ... contenido tarjeta subcategoría ...
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              // Vista de productos (AHORA EN EL MAIN)
              renderProductView(filteredProducts) // Pasar productos filtrados
            )}
          </AnimatePresence>
        </LayoutGroup>
        */}
      </div>

      {/* Header Section - Moved to bottom */}
      <section className="relative py-8 border-b border-white/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-[#FCAA0D] font-medium text-base tracking-wider uppercase mb-2 block">
              MAQUINARIA INDUSTRIAL NUEVA
            </span>

            <p className="text-base text-gray-400 mb-6">
              Explore nuestra colección de maquinaria industrial de última generación, diseñada para
              maximizar la eficiencia de su producción.
            </p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex justify-center gap-3 flex-wrap"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10">
                <Settings2 className="w-4 h-4 text-[#FCAA0D]" />
                <span className="text-white text-sm">
                  Última tecnología
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10">
                <Zap className="w-4 h-4 text-[#FCAA0D]" />
                <span className="text-white text-sm">Máxima eiciencia</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Modales */}
      <AnimatePresence mode="wait">
        {selectedProduct && <div key="product-modal">{renderProductModal()}</div>}
        {showScheduleCallForm && selectedProduct && <div key="schedule-call-modal">{renderScheduleCallForm()}</div>}
        {showContactForm && selectedProduct && <div key="contact-form-modal">{renderContactForm()}</div>}
        {showAuthModal && <AuthModal
          key="auth-modal"
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccessfulAuth={() => {
            setIsUserLoggedIn(true);
            toast({
              title: "Autenticación exitosa",
              description: "Has iniciado sesión correctamente",
            });
          }}
        />}
        {/* Renderizar el simulador financiero siempre en z-index más alto */}
        {showFinancialSimulator && selectedProduct && (
          <div className="relative z-[999999]">
            <FinancialSimulator
              key="financial-simulator"
              productId={selectedProduct.id}
              productPrice={selectedProduct.price}
              productName={selectedProduct.name}
              productSpecs={selectedProduct.specs || {}}
              productImageUrl={selectedProduct.images && selectedProduct.images.length > 0 ? selectedProduct.images[0] : undefined}
              productDescription={selectedProduct.description}
              onClose={() => {
                console.log("Cerrando simulador desde callback");
                setShowFinancialSimulator(false);
              }}
            />
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Convert specs object to array of features
const getFeatures = (specs: Record<string, string>): string[] => {
  return Object.entries(specs).map(([key, value]) => `${key}: ${value}`);
};

// Convert specs object to technical specifications
const getTechnicalSpecs = (
  specs: Record<string, string>,
): Array<{ label: string; value: string }> => {
  return Object.entries(specs).map(([key, value]) => ({
    label: key,
    value: value,
  }));
}
